import React, { useEffect } from 'react';
import { Card, Button, Form, Input, InputNumber, Select, Spin } from 'antd';
import { message } from '../../services/toastMessage';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../components/Layouts/Layout';
import BackButton from '../../components/BackButton';
import AvatarUpload from '../../components/AvatarUpload';
import EmojiTextArea from '../../components/EmojiTextArea';
import PhoneNumberInput from '../../components/PhoneNumberInput';
import { useHostProfile } from '../../contextProviders/HostProfileProvider';
import { uploadPhotos } from '../../services/MediaService';
import { updateMyHostProfile } from '../../services/HostProfileService';
import { HostAccountType } from '../../interfaces/heavyDutyMachinery/IHostProfile';
import { HOST_ACCOUNT_TYPE_OPTIONS, HOST_BUSINESS_CATEGORY_OPTIONS, HOST_LANGUAGE_OPTIONS } from '../../constants/hostProfileOptions';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';

interface HostFormValues {
  accountType: HostAccountType;
  businessCategory?: string;
  businessCategoryOther?: string;
  legalName: string;
  taxId?: string;
  businessRegistrationNumber?: string;
  contactPhone: string;
  billingAddress: string;
  insurancePolicyNumber?: string;
  displayName: string;
  profilePhotoOrLogo?: string;
  tagline?: string;
  languagesSpoken?: string[];
  personalBio?: string;
  companyDescription?: string;
  foundedYear?: number;
  teamSize?: string;
  certifications?: string[];
  brandStory?: string;
}

/**
 * Editing surface for an EXISTING HostProfile's legal OR public block --
 * reached from the "Editar" buttons on Settings' "Perfil de anfitrión"
 * section (HostProfileSection.tsx), one per
 * section (/host/profile/edit/legal, /host/profile/edit/public). Each
 * section is its own independent Form: only the fields for the section
 * named in the URL are ever mounted, so editing one never shows or
 * touches the other's fields -- the update request is a partial
 * IUpdateHostProfileRequest, and HostProfileController's Update endpoint
 * already merges per-field (falling back to the existing profile value
 * for anything not sent), so omitting the other section's fields leaves
 * them untouched server-side.
 */
const HostProfileEditPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { section } = useParams<{ section: string }>();
  const isPublicSection = section === 'public';
  const { hostProfile, loading, refresh } = useHostProfile();
  const [saving, setSaving] = React.useState(false);
  const [form] = Form.useForm<HostFormValues>();
  const watchedAccountType = Form.useWatch('accountType', form);
  const watchedBusinessCategory = Form.useWatch('businessCategory', form);
  const photoUrl = Form.useWatch('profilePhotoOrLogo', form);
  // Account type is only ever edited on the legal section -- on the public
  // section it's read straight off the loaded profile instead of the form,
  // since there's no Form.Item for it there to watch.
  const isBusiness = isPublicSection ? hostProfile?.accountType === 'Business' : watchedAccountType === 'Business';

  useEffect(() => {
    if (section !== 'legal' && section !== 'public') {
      navigate('/host/profile/edit/legal', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  useEffect(() => {
    if (!hostProfile) return;
    form.setFieldsValue({
      accountType: hostProfile.accountType,
      businessCategory: hostProfile.businessCategory,
      businessCategoryOther: hostProfile.businessCategoryOther,
      legalName: hostProfile.legalName,
      taxId: hostProfile.taxId,
      businessRegistrationNumber: hostProfile.businessRegistrationNumber,
      contactPhone: hostProfile.contactPhone,
      billingAddress: hostProfile.billingAddress,
      insurancePolicyNumber: hostProfile.insurancePolicyNumber,
      displayName: hostProfile.displayName,
      profilePhotoOrLogo: hostProfile.profilePhotoOrLogo,
      tagline: hostProfile.tagline,
      languagesSpoken: hostProfile.languagesSpoken ? hostProfile.languagesSpoken.split(',').filter(Boolean) : [],
      personalBio: hostProfile.personalBio,
      companyDescription: hostProfile.companyDescription,
      foundedYear: hostProfile.foundedYear,
      teamSize: hostProfile.teamSize,
      certifications: hostProfile.certifications ? hostProfile.certifications.split(',').filter(Boolean) : [],
      brandStory: hostProfile.brandStory,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostProfile]);

  if (loading || !hostProfile) {
    return (
      <AppLayout>
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      </AppLayout>
    );
  }


  const handleSave = async (values: HostFormValues) => {
    setSaving(true);
    try {
      // Only this section's fields are mounted as Form.Items, so `values`
      // only ever carries them -- the other section's data is simply
      // omitted from the request, and HostProfileController's Update
      // endpoint leaves whatever isn't sent untouched on the saved profile.
      await updateMyHostProfile(
        isPublicSection
          ? {
              displayName: values.displayName,
              profilePhotoOrLogo: values.profilePhotoOrLogo,
              tagline: values.tagline,
              languagesSpoken: values.languagesSpoken?.join(',') || '',
              personalBio: !isBusiness ? values.personalBio : undefined,
              companyDescription: isBusiness ? values.companyDescription : undefined,
              foundedYear: isBusiness ? values.foundedYear : undefined,
              teamSize: isBusiness ? values.teamSize : undefined,
              certifications: isBusiness ? values.certifications?.join(',') || '' : undefined,
              brandStory: isBusiness ? values.brandStory : undefined,
            }
          : {
              accountType: values.accountType,
              businessCategory: isBusiness ? (values.businessCategory as any) : undefined,
              businessCategoryOther: isBusiness && values.businessCategory === 'Other' ? values.businessCategoryOther : undefined,
              legalName: values.legalName,
              taxId: values.taxId,
              businessRegistrationNumber: isBusiness ? values.businessRegistrationNumber : undefined,
              contactPhone: values.contactPhone,
              billingAddress: values.billingAddress,
              insurancePolicyNumber: values.insurancePolicyNumber,
            }
      );
      await refresh();
      message.success(translate(t, TRANSLATIONS.HOST_EDIT_SAVE_SUCCESS));
      navigate('/settings?section=hostProfile');
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HOST_EDIT_SAVE_ERROR));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout>
      <div style={{ width: '100%', maxWidth: 700 }}>
        <div style={{ marginBottom: 8 }}>
          <BackButton fallbackPath="/settings?section=hostProfile" />
        </div>

        <Card title={translate(t, isPublicSection ? TRANSLATIONS.HOST_EDIT_SECTION_PUBLIC : TRANSLATIONS.HOST_EDIT_SECTION_LEGAL)} style={{ borderRadius: 20 }}>
          <>
          <Form form={form} layout="vertical" onFinish={handleSave}>
            {!isPublicSection && (
              <>
                      <Form.Item
                        name="accountType"
                        label={translate(t, TRANSLATIONS.HOST_EDIT_ACCOUNT_TYPE_LABEL)}
                        rules={[{ required: true }]}
                      >
                        <Select
                          options={HOST_ACCOUNT_TYPE_OPTIONS.map((o) => ({ value: o.value, label: translate(t, o.labelKey) }))}
                          disabled={saving}
                        />
                      </Form.Item>
                      {isBusiness && (
                        <Form.Item
                          name="businessCategory"
                          label={translate(t, TRANSLATIONS.HOST_ONBOARDING_BUSINESS_CATEGORY_QUESTION)}
                          rules={[{ required: true }]}
                        >
                          <Select
                            options={HOST_BUSINESS_CATEGORY_OPTIONS.map((o) => ({ value: o.value, label: translate(t, o.labelKey) }))}
                            disabled={saving}
                          />
                        </Form.Item>
                      )}
                      {isBusiness && watchedBusinessCategory === 'Other' && (
                        <Form.Item
                          name="businessCategoryOther"
                          label={translate(t, TRANSLATIONS.HOST_FIELD_BUSINESS_CATEGORY_OTHER_LABEL)}
                          rules={[{ required: true }]}
                        >
                          <Input disabled={saving} />
                        </Form.Item>
                      )}
                      <Form.Item
                        name="legalName"
                        label={translate(t, isBusiness ? TRANSLATIONS.HOST_FIELD_LEGAL_NAME_BUSINESS : TRANSLATIONS.HOST_FIELD_LEGAL_NAME_INDIVIDUAL)}
                        rules={[{ required: true }]}
                      >
                        <Input disabled={saving} />
                      </Form.Item>
                      <Form.Item
                        name="taxId"
                        label={translate(t, isBusiness ? TRANSLATIONS.HOST_FIELD_TAX_ID_BUSINESS : TRANSLATIONS.HOST_FIELD_TAX_ID_INDIVIDUAL)}
                        rules={[{ required: true }]}
                      >
                        <Input disabled={saving} />
                      </Form.Item>
                      {isBusiness && (
                        <Form.Item
                          name="businessRegistrationNumber"
                          label={translate(t, TRANSLATIONS.HOST_FIELD_BUSINESS_REGISTRATION_NUMBER)}
                          rules={[{ required: true }]}
                        >
                          <Input disabled={saving} />
                        </Form.Item>
                      )}
                      <Form.Item name="contactPhone" label={translate(t, TRANSLATIONS.HOST_FIELD_CONTACT_PHONE)} rules={[{ required: true }]}>
                        <PhoneNumberInput disabled={saving} />
                      </Form.Item>
                      <Form.Item name="billingAddress" label={translate(t, TRANSLATIONS.HOST_FIELD_BILLING_ADDRESS)} rules={[{ required: true }]}>
                        <Input disabled={saving} />
                      </Form.Item>
                      <Form.Item
                        name="insurancePolicyNumber"
                        label={translate(t, TRANSLATIONS.HOST_FIELD_INSURANCE_POLICY_NUMBER)}
                        rules={[{ required: isBusiness }]}
                      >
                        <Input disabled={saving} />
                      </Form.Item>
              </>
            )}
            {isPublicSection && (
              <>
                      <Form.Item label={translate(t, TRANSLATIONS.HOST_FIELD_PROFILE_PHOTO)}>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <AvatarUpload
                            value={photoUrl}
                            onChange={(url) => form.setFieldsValue({ profilePhotoOrLogo: url })}
                            uploadFn={(file) => uploadPhotos('hosts', [file]).then((uploaded) => uploaded[0].url)}
                            disabled={saving}
                          />
                        </div>
                      </Form.Item>
                      <Form.Item name="profilePhotoOrLogo" hidden>
                        <Input />
                      </Form.Item>
                      <Form.Item
                        name="displayName"
                        label={translate(t, TRANSLATIONS.HOST_FIELD_PUBLIC_HOST_NAME)}
                        extra={translate(t, TRANSLATIONS.HOST_FIELD_PUBLIC_HOST_NAME_HINT)}
                        rules={[{ required: true }]}
                      >
                        <Input disabled={saving} />
                      </Form.Item>
                      <Form.Item name="tagline" label={translate(t, TRANSLATIONS.HOST_FIELD_TAGLINE)}>
                        <Input maxLength={100} showCount disabled={saving} />
                      </Form.Item>
                      <Form.Item name="languagesSpoken" label={translate(t, TRANSLATIONS.HOST_FIELD_LANGUAGES_SPOKEN)}>
                        <Select
                          mode="multiple"
                          options={HOST_LANGUAGE_OPTIONS.map((o) => ({ value: o.value, label: translate(t, o.labelKey) }))}
                          disabled={saving}
                        />
                      </Form.Item>

                      {!isBusiness && (
                        <Form.Item name="personalBio" label={translate(t, TRANSLATIONS.HOST_FIELD_PERSONAL_BIO)} rules={[{ required: true }]}>
                          <EmojiTextArea rows={4} maxLength={2000} disabled={saving} />
                        </Form.Item>
                      )}
                      {isBusiness && (
                        <>
                          <Form.Item
                            name="companyDescription"
                            label={translate(t, TRANSLATIONS.HOST_FIELD_COMPANY_DESCRIPTION)}
                            rules={[{ required: true }]}
                          >
                            <EmojiTextArea rows={4} maxLength={2000} disabled={saving} />
                          </Form.Item>
                          <div style={{ display: 'flex', gap: 16 }}>
                            <Form.Item name="foundedYear" label={translate(t, TRANSLATIONS.HOST_FIELD_FOUNDED_YEAR)} style={{ flex: 1 }}>
                              <InputNumber min={1800} max={new Date().getFullYear()} style={{ width: '100%' }} disabled={saving} />
                            </Form.Item>
                            <Form.Item name="teamSize" label={translate(t, TRANSLATIONS.HOST_FIELD_TEAM_SIZE)} style={{ flex: 1 }}>
                              <Input placeholder={translate(t, TRANSLATIONS.HOST_FIELD_TEAM_SIZE_PLACEHOLDER)} disabled={saving} />
                            </Form.Item>
                          </div>
                          <Form.Item name="certifications" label={translate(t, TRANSLATIONS.HOST_FIELD_CERTIFICATIONS)}>
                            <Select
                              mode="tags"
                              open={false}
                              disabled={saving}
                              placeholder={translate(t, TRANSLATIONS.HOST_FIELD_CERTIFICATIONS_PLACEHOLDER)}
                            />
                          </Form.Item>
                          <Form.Item name="brandStory" label={translate(t, TRANSLATIONS.HOST_FIELD_BRAND_STORY)}>
                            <EmojiTextArea rows={4} maxLength={4000} disabled={saving} />
                          </Form.Item>
                        </>
                      )}
              </>
            )}
            <Form.Item style={{ marginTop: 16 }}>
              <Button type="primary" shape="round" htmlType="submit" loading={saving}>
                {translate(t, TRANSLATIONS.HOST_EDIT_SAVE_BUTTON)}
              </Button>
            </Form.Item>
          </Form>
          </>
        </Card>
      </div>
    </AppLayout>
  );
};

export default HostProfileEditPage;
